import { logger } from '@adornis/base/logging.js';
import { subscribeClientsForNexusManage } from '../../api/nexus-entity-list-subscriptions.js';
import { confirmDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { Client, WorkflowType } from '@nsc/clients/entities/Client.js';
import { SLAType } from '@nsc/clients/entities/SLAType.js';
import { html } from 'lit';

const showDialogContent = stateful(
  (props: { entry?: Client; onSuccess?: (entry: Client) => void; onCancel?: () => void }) => {
    const [update, setUpdate] = useState(0);
    const doc = useMemo(() => [props.entry ?? new Client({})], [props.entry]);
    const form = useForm(doc, {}, [doc]);

    return html`<div class="flex flex-col gap-3 p-4 min-w-[400px]">
      ${TextInput({
        ...form.field('name'),
        placeholder: 'Name',
      })}
      ${TextInput({ ...form.field('managerPhoneNumber'), placeholder: 'Manager Phone Number' })}
      ${DropdownSelectionInput({
        ...form.field('workflowType'),
        placeholder: 'Workflow Type',
        selectables: Object.entries(WorkflowType)
          .filter(([key]) => !['_class', '_isBaseQLEnum'].includes(key))
          .map(([_, value]) => value),
      })}
      <div class="flex flex-col gap-2 p-2 border border-neutral-300 rounded">
        ${form.document?.slaTypes?.map(
          (sla, i) =>
            html`<div class="flex gap-2">
              ${TextInput({ ...form.field('slaTypes', i, 'priority'), placeholder: 'Priority' })}
              ${TextInput({ ...form.field('slaTypes', i, 'type'), placeholder: 'Type' })}
            </div>`,
        )}
        <button
          class="btn btn-primary btn-sm"
          @click=${() => {
            if (!form.document) return;
            form.document.slaTypes ??= [];
            form.document.slaTypes?.push(new SLAType({ priority: '', type: '' }));
            setUpdate(update + 1);
          }}
        >
          Add SLA Type
        </button>
      </div>
      <div class="divider"></div>
      <div>
        <button
          class="btn btn-primary btn-sm"
          @click=${async () => {
            try {
              await form.validate();
              if (form.document._id) {
                await form.document.save();
              } else {
                await form.document.create();
              }
              props.onSuccess?.(form.document);
            } catch (err) {
              logger.error({ err }, 'Failed to validate form');
            }
          }}
        >
          Save
        </button>
        <button class="btn btn-ghost btn-sm" @click=${() => props.onCancel?.()}>Cancel</button>
      </div>
    </div>`;
  },
);

export const ClientPage = stateful(() => {
  const clients = useObservable(
    () =>
      subscribeClientsForNexusManage()({
        name: 1,
        managerPhoneNumber: 1,
        slaTypes: {
          priority: 1,
          type: 1,
        },
        enabled: 1,
        workflowType: 1,
      }),
    [],
  );

  return html`
    <div class="max-w-6xl mx-auto p-8">
      <h2 class="text-2xl font-bold mb-6">Clients</h2>
      <div class="mb-4 flex justify-between items-center">
        <span class="text-base-content font-semibold">Manage clients and their SLA types</span>
        <button
          class="btn btn-primary btn-sm"
          @click=${async () => {
            await showDialog({
              content: resolve => {
                return showDialogContent({
                  onSuccess: entry => {
                    resolve(entry);
                  },
                  onCancel: () => {
                    resolve(undefined);
                  },
                });
              },
            });
          }}
        >
          + Add Client
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="table w-full">
          <thead>
            <tr class="bg-base-200">
              <th>Client</th>
              <th>Manager Phone Number</th>
              <th>SLA Types</th>
              <th>Workflow Type</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${clients?.map(
              client => html`
                <tr class="hover">
                  <td>${client.name}</td>
                  <td>${client.managerPhoneNumber}</td>
                  <td>
                    ${client.slaTypes?.map(
                      sla => html`<span class="badge badge-primary mr-2">${sla.priority} = ${sla.type}</span>`,
                    )}
                  </td>
                  <td>${client.workflowType ?? '-'}</td>
                  <td class="flex gap-2 justify-center">
                    <button
                      class="btn btn-outline btn-accent btn-xs"
                      @click="${async () => {
                        await showDialog({
                          content: resolve => {
                            return showDialogContent({
                              entry: client as Client,
                              onSuccess: entry => {
                                resolve(entry);
                              },
                              onCancel: () => {
                                resolve(undefined);
                              },
                            });
                          },
                        });
                      }}"
                    >
                      Edit
                    </button>
                    <button
                      class="btn btn-outline btn-error btn-xs"
                      @click="${async () => {
                        if (await confirmDialog('Are you sure you want to delete this client?')) {
                          await client.remove();
                        }
                      }}"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    </div>
  `;
});

export default ClientPage;
